import { RECAPTCHA_SITE_KEY } from "../"
import { CustomWindow } from "../custom"
import { useEffect } from "react"

declare let window: CustomWindow

/**
 * Executes reCAPTCHA to obtain a token for the specified action.
 * @param action - The action for which the reCAPTCHA token is requested.
 * @returns A promise that resolves to the reCAPTCHA token.
 *          If reCAPTCHA is bypassed in local development, a dummy token is returned.
 *          If reCAPTCHA is not available, the promise is rejected with an error message.
 */
export function executeGoogleRecaptcha(action: string): Promise<string> {
  if (RECAPTCHA_SITE_KEY === "dummy_key") {
    // Bypass reCAPTCHA in local development
    return Promise.resolve("dummy_token")
  } else if (window.grecaptcha) {
    // Execute reCAPTCHA in production
    return window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action })
  } else {
    // Handle the case where grecaptcha is not available
    return Promise.reject("reCAPTCHA is not available")
  }
}

/**
 * Custom hook for loading and initializing reCAPTCHA.
 * This hook loads the reCAPTCHA script and initializes it with the provided site key.
 */
export const useRecaptcha = () => {
  useEffect(() => {
    /**
     * Initializes the reCAPTCHA once the script is loaded.
     */
    const initializeRecaptcha = () => {
      window.grecaptcha.ready(() => {})
    }

    /**
     * Loads the reCAPTCHA script dynamically and initializes it.
     */
    const loadRecaptchaScript = () => {
      const script = document.createElement("script")
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`
      script.async = true
      script.defer = true
      script.addEventListener("load", initializeRecaptcha)
      document.body.appendChild(script)
    }
    loadRecaptchaScript()
  }, [])
}
